import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle, ArrowLeft } from "lucide-react";
import { getPaymentStatus } from "@/lib/api";

const MAX_TRIES = 6;

export default function PaymentSuccess() {
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [amount, setAmount] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const res = await getPaymentStatus(sessionId);
        setAmount(res.amount);
        if (res.payment_status === "paid") { setState("paid"); return; }
        if (["expired", "failed"].includes(res.payment_status)) { setState("error"); return; }
      } catch { /* keep trying */ }
      tries += 1;
      if (tries >= MAX_TRIES) { setState("pending"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-ink flex items-center justify-center px-6" data-testid="payment-success-page">
      <div className="noise-overlay" aria-hidden="true" />
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md border border-gold/30 bg-surface p-10 lg:p-14 text-center"
      >
        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-gold mx-auto mb-6 animate-spin" />
            <h1 className="font-serif text-3xl mb-2">Confirming your payment…</h1>
            <p className="text-[#8A8A8A] font-light">One moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-gold mx-auto mb-6" />
            <p className="overline mb-3">Payment received</p>
            <h1 className="font-serif text-4xl mb-4">Thank you{amount ? ` — $${amount}` : ""}.</h1>
            <p className="text-[#8A8A8A] font-light mb-8">
              Your payment is confirmed. We&apos;ll be in touch to finalize your ride.
            </p>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="w-12 h-12 text-gold mx-auto mb-6" />
            <h1 className="font-serif text-3xl mb-3">Payment processing</h1>
            <p className="text-[#8A8A8A] font-light mb-8">
              This can take a moment. If you completed checkout, you&apos;re all set — we&apos;ll confirm shortly.
            </p>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="w-14 h-14 text-red-400 mx-auto mb-6" />
            <h1 className="font-serif text-3xl mb-3">We couldn&apos;t confirm this payment</h1>
            <p className="text-[#8A8A8A] font-light mb-8">Please try again or call us at (516) 262-2059.</p>
          </>
        )}
        <a href="/" data-testid="back-home-btn"
          className="inline-flex items-center gap-2 border border-gold/40 text-gold px-6 py-3 hover:bg-gold hover:text-black transition-colors duration-400">
          <ArrowLeft className="w-4 h-4" /><span className="overline text-gold hover:text-black">Back to site</span>
        </a>
      </motion.div>
    </div>
  );
}
